import capitalize from "lodash/capitalize";
import React, { useCallback, useState } from "react";
import { ChevronDown } from "react-feather";
import { useEditProtocol } from "../../../data/mutations/useEditProtocol";
import { ProtocolItem, Status } from "../../../types";
import { Button } from "../Button";
import { Dialog } from "../Dialog";
import { Fieldset } from "../Fieldset";
import { Form } from "../Form";
import { Label } from "../Label";
import { Select } from "../Select";
import { Textarea } from "../Textarea";
import { TextInput } from "../TextInput";

type EditProtocolDialogProps = {
  protocol: ProtocolItem;
  open: boolean;
  onOpenChange: (value: boolean) => void;
};

export const EditProtocolDialog = ({ protocol, open, onOpenChange }: EditProtocolDialogProps) => {
  const [protocolName, setProtocolName] = useState<string>(protocol.name);
  const [protocolDescription, setProtocolDescription] = useState<string>(protocol.description);
  const [protocolStatus, setProtocolStatus] = useState<Status>(protocol.status);
  const [editProtocol] = useEditProtocol();

  const onProtocolEdit = useCallback(() => {
    return editProtocol({
      variables: {
        id: protocol.id,
        description: protocolDescription,
        name: protocolName,
        status: protocolStatus,
      },
    });
  }, [editProtocol, protocol.id, protocolName, protocolDescription, protocolStatus]);

  return (
    <Dialog.Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <Dialog.Title>Edit protocol</Dialog.Title>
        <Dialog.Description>{`Click save when you're done.`}</Dialog.Description>
        <div className={"mt-5"}>
          <Form>
            <Fieldset>
              <Label htmlFor="name">Name</Label>
              <TextInput
                id="name"
                value={protocolName}
                onValueChange={(e) => {
                  setProtocolName(e.currentTarget.value);
                }}
              />
            </Fieldset>
            <Fieldset>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={protocolDescription}
                onValueChange={(e) => {
                  setProtocolDescription(e.currentTarget.value);
                }}
              />
            </Fieldset>
            <Fieldset>
              <Label htmlFor="status">Status</Label>
              <Select.Select
                value={protocolStatus}
                onValueChange={(value) => setProtocolStatus(value as Status)}>
                <Select.Trigger ariaLabel="status">
                  <Select.Value>{capitalize(protocolStatus)}</Select.Value>
                  <Select.Icon>
                    <ChevronDown />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Content>
                  <Select.Viewport>
                    <Select.Item value={Status.draft}>
                      <Select.ItemText>Draft</Select.ItemText>
                    </Select.Item>
                    <Select.Item value={Status.published}>
                      <Select.ItemText>Published</Select.ItemText>
                    </Select.Item>
                  </Select.Viewport>
                </Select.Content>
              </Select.Select>
            </Fieldset>
          </Form>
        </div>
        <Dialog.Footer>
          <Dialog.Close>
            <Button
              variant="primary"
              text="Save protocol"
              onClick={() => {
                onProtocolEdit();
              }}
            />
          </Dialog.Close>
          <Dialog.Close>
            <Button variant="outline" text="Cancel" />
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Dialog>
  );
};
